import React, { useState } from 'react';

import { QuestionMarkCircleIcon } from '@heroicons/react/outline';
import { Switch, Tooltip } from '@mui/material';
import { useFormikContext } from 'formik';
import { mutate } from 'swr';

import ChartTitleField from '@components/experiments/plotDisplay/fields/ChartTitleField';
import FullWidthToggleField from '@components/experiments/plotDisplay/fields/FullWidthToggleField';
import ThemeFieldGroup from '@components/experiments/plotDisplay/groups/ThemeFieldGroup';
import { useSwitchStyles } from '@components/SwitchStyles';

import { useExperimentAnnotationContext } from '@contexts/ExperimentAnnotationContext';

import { AnalysisParameters, SeuratDifferentialExpressionAnalysisParameters } from '@models/AnalysisParameters';
import Experiment from '@models/Experiment';
import Plot from '@models/Plot';
import { ViolinPlotDisplayOptionFormValues } from '@models/PlotDisplayOption';

import Endpoints from '@services/Endpoints';

import CellGroups from '../fields/CellGroups';
import AssaySummaryLegendFieldGroupV3 from '../groups/AssaySummaryLegendFieldGroupV3';

type Props = {
    analysisParameters?: AnalysisParameters | null;
    experiment: Experiment;
    plot: Plot;
    setDisplaySaveDisabled: (value: boolean) => void;
    setUpdateGroupsDisabled: (value: boolean) => void;
    updateGroupsDisabled: boolean;
};
const ViolinPlotDisplayFields = ({
    analysisParameters,
    experiment,
    plot,
    setDisplaySaveDisabled,
    setUpdateGroupsDisabled,
    updateGroupsDisabled,
}: Props) => {
    const { values, setFieldValue } = useFormikContext<ViolinPlotDisplayOptionFormValues>();
    const [splitByCluster, setSplitByCluster] = useState(Boolean(values?.annotation_set_id) ?? false);
    const switchStyles = useSwitchStyles();
    const { annotationSets } = useExperimentAnnotationContext();
    const typedParameters = analysisParameters as SeuratDifferentialExpressionAnalysisParameters;
    const isCleanedWorkflow = experiment?.is_cleaned_workflow ?? false;

    const getTooltipText = () => {
        switch (plot?.analysis_type) {
            case 'seurat_module_score':
                return 'Horizontally compress violins if 75% or more of the cells have zero activity. This helps highlight gene module activity patterns more clearly by reducing the visual impact of groups with predominantly zeros values, allowing you to focus on areas with more significant activity differences.';
            case 'seurat_marker_expression':
            default:
                return 'Horizontally compress violins if 75% or more of the cells have zero expression. This helps to highlight gene expression patterns more clearly by reducing the visual impact of groups with predominantly zero values, allowing you to focus on areas with more significant expression differences.';
        }
    };
    return (
        <>
            <FullWidthToggleField />
            <ChartTitleField placeholder={plot.analysis?.name} />
            <div className="form-field !flex flex-row items-center justify-between">
                <div className="flex flex-row items-center">
                    <span className="text-dark text-lg font-semibold tracking-tight">Use horizontal compression</span>
                    <Tooltip title={getTooltipText()} placement="right" arrow className="ml-1">
                        <span>
                            <QuestionMarkCircleIcon className="h-4 w-4" />
                        </span>
                    </Tooltip>
                </div>
                <Switch
                    sx={switchStyles}
                    checked={values.custom_options_json?.useHorizontalCompression ?? true}
                    name="custom_options_json.useHorizontalCompression"
                    onChange={(e) => {
                        setFieldValue('custom_options_json.useHorizontalCompression', e.target.checked);
                    }}
                />
            </div>
            <CellGroups
                experiment={experiment}
                plot={plot}
                analysisParameters={typedParameters}
                annotationSets={annotationSets}
                isCleanedWorkflow={isCleanedWorkflow}
            >
                <CellGroups.Header />
                <CellGroups.Description />
                <CellGroups.GroupByClusterField
                    checked={splitByCluster}
                    onChange={(e) => {
                        setSplitByCluster((prev) => !prev);
                        if (!e.target.checked) {
                            setFieldValue('annotation_set_id', null);
                        }
                    }}
                />
                {splitByCluster && (
                    <CellGroups.AnnotationSetField
                        value={values.annotation_set_id}
                        onChange={(e) => setFieldValue('annotation_set_id', e.target.value)}
                    />
                )}
                <CellGroups.VariablesField />
                <CellGroups.LatentVariablesField />
                <CellGroups.SubmitButton
                    disabled={updateGroupsDisabled}
                    onClick={() => {
                        mutate(
                            Endpoints.lab.experiment.plot.groupsV3({
                                experimentId: experiment.uuid,
                                plotId: plot.uuid,
                            }),
                        );
                    }}
                />
            </CellGroups>
            <ThemeFieldGroup hideStyle />
            <AssaySummaryLegendFieldGroupV3
                description="Click and drag the legend items to reorder. Plot will only show groups included in the analysis"
                experiment={experiment}
                ignoreEmpty
                isSortable={true}
                plot={plot}
                setDisplaySaveDisabled={setDisplaySaveDisabled}
                setUpdateGroupsDisabled={setUpdateGroupsDisabled}
            />
        </>
    );
};

export default ViolinPlotDisplayFields;
